/* src/components/tutor/tutor_announcements/TutorAnnouncementsLogic.js */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";

// ------------------------------
// Helpers
// ------------------------------
async function getMe() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in.");

  const snap = await getDoc(doc(db, "users", user.uid));
  const data = snap.exists() ? (snap.data() || {}) : {};

  return {
    uid: user.uid,
    name: data.name || user.displayName || "User",
    email: data.email || user.email || "",
    role: data.role || "tutor",
  };
}

function withCreatedAtMs(id, data) {
  const createdAtMs =
    data?.createdAt && typeof data.createdAt.toMillis === "function"
      ? data.createdAt.toMillis()
      : null;
  return { id, ...data, createdAtMs };
}

// ------------------------------
// 1) Announcements feed (Tutor scoped)
// ------------------------------
export function subscribeTutorAnnouncements(uid, onRows) {
  if (!uid) return () => {};

  const qAll = query(
    collection(db, "announcements"),
    where("audienceType", "==", "all")
  );

  const qUsers = query(
    collection(db, "announcements"),
    where("audienceType", "==", "users"),
    where("audienceUserIds", "array-contains", uid)
  );

  const sourceAll = new Map();
  const sourceUsers = new Map();

  const recompute = () => {
    const merged = new Map();
    for (const [id, v] of sourceAll) merged.set(id, v);
    for (const [id, v] of sourceUsers) merged.set(id, v);
    onRows(Array.from(merged.values()));
  };

  const unsubAll = onSnapshot(
    qAll,
    (snap) => {
      sourceAll.clear();
      snap.docs.forEach((d) => sourceAll.set(d.id, withCreatedAtMs(d.id, d.data() || {})));
      recompute();
    },
    (err) => {
      console.error("subscribeTutorAnnouncements(all) error:", err);
      onRows([]);
    }
  );

  const unsubUsers = onSnapshot(
    qUsers,
    (snap) => {
      sourceUsers.clear();
      snap.docs.forEach((d) => sourceUsers.set(d.id, withCreatedAtMs(d.id, d.data() || {})));
      recompute();
    },
    (err) => {
      console.error("subscribeTutorAnnouncements(users) error:", err);
      onRows([]);
    }
  );

  return () => {
    unsubAll();
    unsubUsers();
  };
}

// ------------------------------
// 2) Categories (read-only for tutors)
// ------------------------------
export function subscribeCategories(onRows) {
  const q = query(collection(db, "announcement_categories"), orderBy("name", "asc"));
  return onSnapshot(
    q,
    (snap) => {
      onRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
    },
    (err) => {
      console.error("subscribeCategories error:", err);
      onRows([]);
    }
  );
}

// ------------------------------
// 3) Read receipts (tutor marks read)
// ------------------------------
export async function markAnnouncementRead(announcementId) {
  const me = await getMe();
  const ref = doc(db, "announcements", announcementId, "reads", me.uid);
  await setDoc(
    ref,
    {
      uid: me.uid,
      userName: me.name,
      userEmail: me.email,
      userRole: me.role,
      readAt: serverTimestamp(),
      method: "manual",
    },
    { merge: true }
  );
}

export function subscribeMyReadStatus(announcementId, onStatus) {
  const uid = auth.currentUser?.uid;
  if (!uid) return () => {};

  const ref = doc(db, "announcements", announcementId, "reads", uid);
  return onSnapshot(
    ref,
    (snap) => onStatus(snap.exists() ? (snap.data() || null) : null),
    (err) => {
      console.error("subscribeMyReadStatus error:", err);
      onStatus(null);
    }
  );
}

export function subscribeReadsCount(announcementId, onCount) {
  const ref = collection(db, "announcements", announcementId, "reads");
  return onSnapshot(
    ref,
    (snap) => onCount(snap.size),
    (err) => {
      console.error("subscribeReadsCount error:", err);
      onCount(0);
    }
  );
}

// ------------------------------
// 4) Comments
// ------------------------------
export function subscribeComments(announcementId, onRows) {
  const q = query(
    collection(db, "announcements", announcementId, "comments"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    (err) => {
      console.error("subscribeComments error:", err);
      onRows([]);
    }
  );
}

export async function addComment(announcementId, text, parentId = null) {
  const me = await getMe();

  let commentText = text;
  let commentParentId = parentId;

  // Allow { text, parentId } signature too
  if (typeof text === "object" && text !== null) {
    commentText = text.text;
    commentParentId = text.parentId ?? null;
  }

  const clean = (commentText || "").replace(/\u200B/g, "").replace(/\u00A0/g, " ").trim();
  if (!clean) throw new Error("Comment cannot be empty.");

  await addDoc(collection(db, "announcements", announcementId, "comments"), {
    uid: me.uid,
    userName: me.name,
    userEmail: me.email,
    userRole: me.role,
    text: clean,
    parentId: commentParentId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteComment(announcementId, commentId) {
  await deleteDoc(doc(db, "announcements", announcementId, "comments", commentId));
}
